//src/tools/rpc-bridge.ts

/**
 * RpcBridge is an interface that provides mechanisms for handling remote procedure calls and event-based communication.
 *
 * @interface
 */
interface RpcBridge {
    call: <T extends any[]>(method: string, ...args: T) => Promise<any>;
    expose: (method: string, fn: Function) => void;
    on: (eventName: string, handler: (payload: any) => void) => void;
    once: (eventName: string, handler: (payload: any) => void) => void;
    off: (eventName: string, handler?: (payload: any) => void) => void;
    emit: (eventName: string, payload?: any) => void;
    send: (message: any) => void;
    destroy: () => void;
}

/**
 * Represents a Remote Procedure Call (RPC) request.
 *
 * This interface defines the structure of a standard RPC request object.
 * It is used to call a specific method on a remote server or system with
 * a set of provided arguments.
 *
 * Properties:
 * - `__rpc__`: A boolean flag that identifies the object as an RPC request.
 * - `type`: Specifies the type of the request, which is always 'rpc' for this interface.
 * - `method`: The name of the remote method to be invoked.
 * - `id`: A unique identifier used to track the request and its response.
 * - `args`: An array of arguments to be passed to the remote method.
 */
interface RpcRequest {
    __rpc__: true;
    type: 'rpc';
    method: string;
    id: string;
    args: any[];
}

/**
 * Represents the structure of a response in an RPC (Remote Procedure Call) communication.
 *
 * This interface is designed to indicate the format of a properly formed RPC response,
 * containing either a successful result or an error indication.
 *
 * Properties:
 * - __rpc__: A boolean flag indicating that this object adheres to the RPC response structure.
 * - type: The type of response, which is always set to 'result' for this interface.
 * - id: A unique identifier corresponding to the request that this response is associated with.
 * - result: An optional property representing the successful outcome of the RPC operation. This could
 *           contain any valid data or object returned from the invoked operation.
 * - error: An optional string describing the error that occurred during the operation,
 *          if the operation failed.
 */
interface RpcResponse {
    __rpc__: true;
    type: 'result';
    id: string;
    result?: any;
    error?: string;
}

/**
 * Interface representing an RPC (Remote Procedure Call) event structure.
 * It is used to define the details of an event that occurs during
 * an RPC operation.
 *
 * Properties:
 * - `__rpc__` (true): Represents the indication that this object pertains to RPC. Always `true`.
 * - `type` ('event'): Specifies that the object type is an event.
 * - `event` (string): The name or identifier of the event being triggered.
 * - `payload` (optional): Additional data or context associated with the event. Can be of any type.
 */
interface RpcEvent {
    __rpc__: true;
    type: 'event';
    event: string;
    payload?: any;
}

/**
 * Represents an RPC message which can be one of the following types: RpcRequest, RpcResponse, or RpcEvent.
 *
 * This type is a union type that allows handling of distinct categories of messages in a Remote Procedure Call (RPC) system:
 * - RpcRequest: Represents a request message sent from a client to a server.
 * - RpcResponse: Represents a response message sent from a server back to the client.
 * - RpcEvent: Represents an event notification sent from a server to a client or between peers.
 */
type RpcMessage = RpcRequest | RpcResponse | RpcEvent;

/**
 * Generates a unique ID using the most reliable available method.
 *
 * Priority:
 * 1) crypto.randomUUID (when available)
 * 2) RFC4122 v4 via crypto.getRandomValues
 * 3) Time + Math.random fallback
 */
function generateId(): string {
    const c = (globalThis as any).crypto as Crypto | undefined;
    // 1) Prefer native randomUUID when available
    if (c && typeof (c as any).randomUUID === 'function') {
        try {
            return (c as any).randomUUID();
        } catch (_) {
            // fall through to the next method
        }
    }

    // 2) Use crypto.getRandomValues to create a RFC4122 v4 UUID
    if (c && typeof c.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        c.getRandomValues(bytes);
        // Per RFC4122 section 4.4
        bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
        bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10

        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20)}`;
    }

    // 3) Fallback: time + random (not cryptographically strong, but unique enough for IDs)
    return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Provides a utility for communication between windows using a remote procedure call (RPC)
 * bridge. This function connects to a target window and allows sending RPC requests,
 * emitting events, and managing listeners for invoked methods or events.
 *
 * @param {Window} targetWindow - The target window to establish an RPC bridge connection with.
 *                                This acts as the destination window for all outgoing messages.
 * @param {string} targetOrigin - The origin of the target window. This is used for security to ensure
 *                                that messages are only sent and received to/from trusted origins.
 * @param {number} [timeout=50000] - The default timeout in milliseconds for RPC requests.
 *                                   If the target does not respond within this time, the RPC call is rejected.
 * @returns {RpcBridge} A collection of methods to handle RPC communication, including requesting remote calls,
 *                      exposing local methods, managing event listeners, and emitting custom events.
 */
export function useRpcBridge(targetWindow: Window | MessagePort, targetOrigin: string = '*', timeout: number = 50000): RpcBridge {
    const
        handlers = new Map<string, Function>(),
        listeners = new Map<string, ((payload: any) => void)[]>(),
        pending = new Map<string, {
            resolve: (res: any) => void,
            reject: (err: any) => void,
            timer?: ReturnType<typeof setTimeout>
        }>(),

        isWindow = typeof targetWindow.postMessage === 'function' &&
            'location' in targetWindow,

        /**
         * Sends a message to the specified target window or message port.
         *
         * This function checks if the target window supports the `postMessage` method
         * and then sends the provided message. The target can either be a window object
         * that implements the `postMessage` API or a message port.
         *
         * If the `targetWindow` is an instance of `Window`, it sends the message using
         * the provided target origin. Otherwise, it assumes the target is a `MessagePort`
         * and utilizes its `postMessage` method.
         *
         * If `targetWindow` is not defined or does not support the `postMessage` method,
         * the message will not be sent.
         *
         * @param {any} message - The message to be sent to the target.
         * @returns {void}
         */
        send = (message: any): void => {
            if (!targetWindow) return;
            if ('postMessage' in targetWindow) {
                if (isWindow) {
                    targetWindow.postMessage(message, targetOrigin || '*');
                } else {
                    (targetWindow as MessagePort).postMessage(message);
                }
            }
        },

        /**
         * Sends a remote procedure call (RPC) request to a target window and returns a promise
         * that resolves with the response or rejects on timeout or error.
         *
         * @template T - A tuple representing the expected types of the arguments.
         * @param {string} method - The name of the method being called on the target.
         * @returns {Promise<any>} A promise that resolves with the RPC response or rejects
         *                         if the request times out or an error occurs.
         * @throws {Error} If the RPC request exceeds the predefined timeout.
         */
        call = <T extends any[]>(method: string, ...args: T): Promise<any> => {
            const id = generateId();
            const message: RpcRequest = {
                __rpc__: true,
                type: 'rpc',
                method,
                id,
                args,
            };
            send(message);

            return new Promise((resolve, reject) => {
                const timer = setTimeout(() => {
                    pending.delete(id);
                    reject(new Error(`[RPC] Timeout: ${method}`));
                }, timeout);
                pending.set(id, {resolve, reject, timer});
            });
        },

        /**
         * Exposes a method by associating a given function with a specified method name.
         * The method name acts as a key to retrieve and execute the corresponding function.
         *
         * @param {string} method - The name of the method to be exposed.
         * @param {Function} fn - The function to be associated with the specified method name.
         */
        expose = (method: string, fn: Function) => {
            handlers.set(method, fn);
        },

        /**
         * Registers an event listener for a specified event.
         *
         * @param {string} eventName - The name of the event to listen for.
         * @param {(payload: any) => void} handler - The callback function to execute when the event is triggered.
         *                                           It receives the event's payload as its argument.
         */
        on = (eventName: string, handler: (payload: any) => void) => {
            if (!listeners.has(eventName)) listeners.set(eventName, []);
            listeners.get(eventName)!.push(handler);
        },

        /**
         * Emits an event with the specified name and optional payload. The event is
         * wrapped in a standardized `RpcEvent` object and sent to the `targetWindow`
         * using `send`.
         *
         * @param {string} eventName - The name of the event to emit.
         * @param {any} [payload] - An optional payload to include with the event.
         */
        emit = (eventName: string, payload?: any) => {
            const message: RpcEvent = {
                __rpc__: true,
                type: 'event',
                event: eventName,
                payload
            };
            send(message);
        },
        /**
         * Removes a specific event listener or all listeners for a given event name.
         *
         * @param {string} eventName - The name of the event for which the listener(s) should be removed.
         * @param {Function} [handler] - The specific event handler to remove. If omitted, all listeners for the event will be removed.
         */
        off = (eventName: string, handler?: (payload: any) => void) => {
            if (!listeners.has(eventName)) return;
            if (!handler) {
                listeners.delete(eventName);
            } else {
                const arr = listeners.get(eventName)!;
                listeners.set(eventName, arr.filter(fn => fn !== handler));
            }
        },

        /**
         * Registers an event handler for a given event name that is triggered only once.
         * Once the event is triggered, the handler is executed and then automatically removed.
         *
         * @param {string} eventName - The name of the event to listen for.
         * @param {(payload: any) => void} handler - The callback function to execute when the event occurs.
         *                                           It receives the event payload as its parameter.
         */
        once = (eventName: string, handler: (payload: any) => void) => {
            const wrapper = (payload: any) => {
                off(eventName, wrapper);
                handler(payload);
            };
            on(eventName, wrapper);
        },

        /**
         * Handles incoming MessageEvent objects, processes remote procedure call (RPC) messages,
         * executes corresponding handlers, and manages asynchronous RPC responses and events.
         *
         * This function processes three types of messages:
         * - 'rpc': Executes a specified remote procedure by delegating it to a handler.
         * - 'result': Handles the result or error from a previously invoked RPC.
         * - 'event': Dispatches an event to all registered listeners for that event type.
         *
         * Key Responsibilities:
         * - Verifies the origin and structure of the incoming message to ensure security.
         * - Executes the RPC handler function for the given method, passing provided arguments.
         * - Responds asynchronously with the result or errors from the handler execution.
         * - Resolves or rejects promises corresponding to pending RPC calls based on received results.
         * - Invokes all registered listeners for a specified event type when an event occurs.
         *
         * @param {MessageEvent} event - The message event received from another window or context.
         * Contains the data required to process remote procedure calls, results, or events.
         */
        onMessage = (event: MessageEvent) => {
            const data: RpcMessage = event.data;
            if (!data?.__rpc__ || (targetOrigin !=='*' && targetOrigin !== event.origin)) return;

            switch (data.type) {
                case 'rpc': {
                    const {id, method, args} = data;
                    const fn = handlers.get(method);
                    if (fn) {
                        Promise.resolve()
                            .then(() => fn(...args))
                            .then(result => {
                                send({__rpc__: true, type: 'result', id, result});
                            })
                            .catch(error => {
                                send({
                                    __rpc__: true,
                                    type: 'result',
                                    id,
                                    error: String(error)
                                });
                            });
                    } else
                        send({__rpc__: true, type: 'result', id, error: `No handler for ${method}`});
                    break;
                }

                case 'result': {
                    const {id, result, error} = data;
                    const entry = pending.get(id);
                    if (entry) {
                        clearTimeout(entry.timer);
                        error ? entry.reject(error) : entry.resolve(result);
                        pending.delete(id);
                    }
                    break;
                }

                case 'event': {
                    const handlers = listeners.get(data.event);
                    if (handlers) {
                        handlers.forEach(fn => fn(data.payload));
                    }
                    break;
                }
            }
        },
        destroy = () => {
            window.removeEventListener('message', onMessage);
            pending.forEach(entry => clearTimeout(entry.timer));
            handlers.clear();
            listeners.clear();
            pending.clear();
        };

    window.addEventListener('message', onMessage);
    return {call, expose, on, once, off, emit, send, destroy};
}